<template>
  <div class="section-terms">
    <div class="section-terms__header" @click="$emit('toggle')">
      <i class="mdi mdi-drag section-terms__drag-handle" />
      <AppLabel color="success" text="ボタン・規約セクション" />
      <span class="section-terms__spacer" />
      <i :class="collapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'" />
    </div>

    <div v-if="!collapsed" class="section-terms__content">
      <div class="section-terms__field">
        <div class="label section-terms__label-row">
          規約
          <span class="badge badge-danger">必須</span>
        </div>
        <div class="section-terms__control">
          <EditableComponent
            :value="section.page_rules"
            placeholder="内容を入力"
            :disabled="disabled"
            @input="updateField('page_rules', $event)"
          />
          <div v-if="isRuleEmpty" class="section-terms__error">テキストを入力してください。</div>
        </div>
      </div>

      <div class="section-terms__field">
        <div class="label mb-x1">
          <span>色の設定</span>
        </div>
        <div class="section-terms__control">
          <el-switch :value="isColorSettingVisible" :disabled="disabled" @change="toggleColorSetting" />
        </div>
      </div>

      <div v-if="isColorSettingVisible" class="section-terms__colors">
        <div class="section-terms__color-item section-terms__color-item--apply-button">
          <div class="label">応募ボタン</div>
          <ColorPicker
            :value="section.apply_button_background_color"
            :disabled="disabled"
            :showHex="showHex"
            @input="updateField('apply_button_background_color', $event)"
          />
        </div>
        <div class="section-terms__color-item section-terms__color-item--apply-button-text">
          <div class="label">応募ボタン（テキスト）</div>
          <ColorPicker
            :value="section.apply_button_text_color"
            :disabled="disabled"
            :showHex="showHex"
            @input="updateField('apply_button_text_color', $event)"
          />
        </div>
        <div class="section-terms__color-item section-terms__color-item--rule-button">
          <div class="label">規約ボタン</div>
          <ColorPicker
            :value="section.rule_button_background_color"
            :disabled="disabled"
            :showHex="showHex"
            @input="updateField('rule_button_background_color', $event)"
          />
        </div>
        <div class="section-terms__color-item section-terms__color-item--apply-text">
          <div class="label">応募文章</div>
          <ColorPicker
            :value="section.apply_text_color"
            :disabled="disabled"
            :showHex="showHex"
            @input="updateField('apply_text_color', $event)"
          />
        </div>
        <div class="section-terms__color-item section-terms__color-item--apply-warning">
          <div class="label">応募文章（注意事項）</div>
          <ColorPicker
            :value="section.apply_warning_text_color"
            :disabled="disabled"
            :showHex="showHex"
            @input="updateField('apply_warning_text_color', $event)"
          />
        </div>
        <div class="section-terms__color-item section-terms__color-item--apply-error">
          <div class="label">応募文章（認証エラー）</div>
          <ColorPicker
            :value="section.apply_error_text_color"
            :disabled="disabled"
            :showHex="showHex"
            @input="updateField('apply_error_text_color', $event)"
          />
        </div>
        <div class="section-terms__color-item section-terms__color-item--rule-button-text">
          <div class="label">規約ボタン（テキスト）</div>
          <ColorPicker
            :value="section.rule_button_text_color"
            :disabled="disabled"
            :showHex="showHex"
            @input="updateField('rule_button_text_color', $event)"
          />
        </div>
      </div>

      <div v-if="isColorSettingVisible" class="section-terms__field">
        <div class="label">背景色</div>
        <div class="section-terms__control">
          <ColorPicker
            :value="section.background_color"
            :disabled="disabled"
            :showHex="showHex"
            @input="updateField('background_color', $event)"
          />
        </div>
      </div>

      <div v-if="isColorSettingVisible" class="section-terms__actions">
        <el-button plain :disabled="disabled" @click="resetColors">
          <i class="mdi mdi-restore" />
          色をリセット
        </el-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { computed, defineComponent, PropType, ref, watch } from 'vue'

import AppLabel from '@/client/components/_atoms/app_label'
import ColorPicker from '@/client/components/_atoms/color_picker'
import EditableComponent from '@/client/components/_atoms/editable/editable.vue'
import { TermsSectionData } from '@/client/components/_molecules/section_terms/types'

const DEFAULT_APPLY_BUTTON_BACKGROUND_COLOR = '#009ccb'
const DEFAULT_APPLY_BUTTON_TEXT_COLOR = '#ffffff'
const DEFAULT_APPLY_TEXT_COLOR = '#000000'
const DEFAULT_APPLY_WARNING_TEXT_COLOR = '#ffa726'
const DEFAULT_APPLY_ERROR_TEXT_COLOR = '#ef5350'
const DEFAULT_RULE_BUTTON_BACKGROUND_COLOR = '#ffffff'
const DEFAULT_RULE_BUTTON_TEXT_COLOR = '#333333'
const DEFAULT_RULE_SECTION_BACKGROUND_COLOR = '#FFFFFF'
export default defineComponent({
  name: 'SectionTerms',
  components: {
    AppLabel,
    ColorPicker,
    EditableComponent
  },
  props: {
    section: {
      type: Object as PropType<TermsSectionData>,
      required: true
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showHex: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggle', 'update'],
  setup(props, { emit }) {
    const isRuleEmpty = computed(() => !props.section.page_rules || !props.section.page_rules.trim())
    const isColorSettingVisible = ref(false)
    const sectionKey = computed(() => `${props.section.section_id ?? 'temp'}-${props.section.step_order}`)

    const hasColorSettings = (section: TermsSectionData) =>
      [
        section.apply_button_background_color,
        section.apply_button_text_color,
        section.apply_text_color,
        section.apply_warning_text_color,
        section.apply_error_text_color,
        section.rule_button_background_color,
        section.rule_button_text_color,
        section.background_color
      ].some(value => value !== null && value !== undefined)

    watch(
      sectionKey,
      () => {
        isColorSettingVisible.value = hasColorSettings(props.section)
      },
      { immediate: true }
    )

    const updateField = (field: keyof TermsSectionData, value: unknown) => {
      if (props.disabled) {
        return
      }
      emit('update', { ...props.section, [field]: value })
    }

    const toggleColorSetting = (value: boolean) => {
      if (props.disabled) {
        return
      }
      isColorSettingVisible.value = value
    }

    const resetColors = () => {
      if (props.disabled) {
        return
      }
      emit('update', {
        ...props.section,
        apply_button_background_color: DEFAULT_APPLY_BUTTON_BACKGROUND_COLOR,
        apply_button_text_color: DEFAULT_APPLY_BUTTON_TEXT_COLOR,
        apply_text_color: DEFAULT_APPLY_TEXT_COLOR,
        apply_warning_text_color: DEFAULT_APPLY_WARNING_TEXT_COLOR,
        apply_error_text_color: DEFAULT_APPLY_ERROR_TEXT_COLOR,
        rule_button_background_color: DEFAULT_RULE_BUTTON_BACKGROUND_COLOR,
        rule_button_text_color: DEFAULT_RULE_BUTTON_TEXT_COLOR,
        background_color: DEFAULT_RULE_SECTION_BACKGROUND_COLOR
      })
    }

    return {
      isRuleEmpty,
      isColorSettingVisible,
      updateField,
      toggleColorSetting,
      resetColors
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.section-terms {
  background: $text-color-white;
  border: 1px solid $border-color;
  border-radius: $round;

  .mdi-help-circle {
    margin-left: $spacing-unit;
    font-size: 18px;
  }

  &__header {
    display: flex;
    gap: $spacing-unit;
    align-items: center;
    padding: $spacing-unit * 2;
    cursor: pointer;
    -webkit-user-select: none;
    user-select: none;
  }

  &__drag-handle {
    font-size: 18px;
    color: $sub-text-color;
    cursor: grab;
  }

  &__spacer {
    flex: 1;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: $spacing-unit * 2;
    padding: $spacing-unit * 2;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: $spacing-unit;
  }

  &__label-row {
    display: inline-flex;
    flex-wrap: wrap;
    gap: $spacing-unit;
    align-items: center;
    max-width: 100%;

    .badge.badge-danger,
    .mdi-help-circle {
      margin-left: 0;
    }
  }

  &__control {
    display: flex;
    flex-direction: column;
    gap: $spacing-unit;
  }

  &__colors {
    display: grid;
    grid-template-areas:
      'apply-button apply-button-text .'
      'apply-text apply-warning apply-error'
      'rule-button rule-button-text .';
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: $spacing-unit * 2;
  }

  &__color-item {
    display: flex;
    flex-direction: column;
    gap: $spacing-unit;
  }

  &__color-item--apply-button {
    grid-area: apply-button;
  }

  &__color-item--apply-button-text {
    grid-area: apply-button-text;
  }

  &__color-item--rule-button {
    grid-area: rule-button;
  }

  &__color-item--apply-text {
    grid-area: apply-text;
  }

  &__color-item--apply-warning {
    grid-area: apply-warning;
  }

  &__color-item--apply-error {
    grid-area: apply-error;
  }

  &__color-item--rule-button-text {
    grid-area: rule-button-text;
  }

  &__actions {
    display: flex;
    justify-content: flex-start;
  }

  &__error {
    margin-top: $spacing-unit;
    color: $danger-color;
  }

  // グローバルデザインだと崩れるため、display: inline-block に変更
  .badge.badge-danger {
    position: initial;
    display: inline-block;
  }
}
</style>
